"use client";

import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { analyzeMelodyTaps, type MelodyTapAnalysis, type MelodyTapRawGesture } from "../lib/melody-tap";
import { BpmNumberInput } from "./BpmNumberInput";

type Props = {
  bpm: number;
  count: number | null;
  bpmConfirmed: boolean;
  recommendationApplied: boolean;
  onStartAudio: () => Promise<boolean>;
  onStopAudio: () => void;
  onApply: (analysis: MelodyTapAnalysis) => void;
  onRestore: () => void;
  onBack: () => void;
  purpose?: "settings" | "rhythm-refine" | "rhythm-first";
  onCreate?: (rawGestures: MelodyTapRawGesture[], analysis: MelodyTapAnalysis) => void;
  pickupEnabled?: boolean;
  onPickupChange?: (enabled: boolean) => void;
  countInBeats?: number;
  targetLabel?: string;
  tapCount?: number;
  onTapTempo?: () => void;
  onBpmChange?: (bpm: number) => void;
};

type CaptureStage = "intro" | "counting" | "tapping" | "result";
type ActiveGesture = { pointerId: number; startTime: number };

export function MelodyTapGuide(props: Props) {
  const [stage, setStage] = useState<CaptureStage>("intro");
  const [rawGestures, setRawGestures] = useState<MelodyTapRawGesture[]>([]);
  const [active, setActive] = useState<ActiveGesture | null>(null);
  const [visualNow, setVisualNow] = useState(0);
  const sequenceRef = useRef(1);
  const [barStartedAt, setBarStartedAt] = useState<number | null>(null);
  const analysis = useMemo(() => analyzeMelodyTaps(rawGestures, props.bpm), [props.bpm, rawGestures]);
  const beatMs = 60_000 / props.bpm;
  const countInBeats = Math.max(1, props.countInBeats ?? 4);
  const createMode = props.purpose === "rhythm-refine" || props.purpose === "rhythm-first";
  const purposeLabel = props.purpose === "rhythm-refine" ? "RHYTHM REFINE" : props.purpose === "rhythm-first" ? "RHYTHM FIRST" : "MELODY TAP";
  const countBeat = props.count === null ? -1 : Math.max(0, countInBeats - props.count);
  const liveBeat = barStartedAt === null ? 1 : Math.floor(Math.max(0, visualNow - barStartedAt) / beatMs) % countInBeats + 1;
  const acceptsCountInput = stage === "counting" && Boolean(props.pickupEnabled);
  const canTap = stage === "tapping" || acceptsCountInput;

  useEffect(() => {
    if (stage === "intro" || stage === "result") return;
    let frame = 0;
    const update = () => {
      setVisualNow(performance.now());
      frame = window.requestAnimationFrame(update);
    };
    frame = window.requestAnimationFrame(update);
    return () => window.cancelAnimationFrame(frame);
  }, [stage]);

  async function start(clear = false) {
    props.onStopAudio();
    if (clear) {
      setRawGestures([]);
      sequenceRef.current = 1;
    }
    setActive(null);
    setStage("counting");
    const started = await props.onStartAudio();
    setBarStartedAt(started ? performance.now() : null);
    setStage(started ? "tapping" : "intro");
  }

  function finishActive(endTime: number) {
    if (!active) return;
    setRawGestures((current) => [...current, {
      id: sequenceRef.current++,
      startTime: active.startTime,
      endTime: Math.max(active.startTime + 1, endTime),
    }]);
    setActive(null);
  }

  function pointerDown(event: ReactPointerEvent<HTMLButtonElement>) {
    if (!canTap || active) return;
    const rawStartTime = performance.now();
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setVisualNow(rawStartTime);
    setActive({ pointerId: event.pointerId, startTime: rawStartTime });
    event.currentTarget.classList.add("holding");
  }

  function pointerUp(event: ReactPointerEvent<HTMLButtonElement>) {
    if (!active || event.pointerId !== active.pointerId) return;
    const rawEndTime = performance.now();
    event.preventDefault();
    finishActive(rawEndTime);
    event.currentTarget.classList.remove("holding");
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  }

  function pointerCancel(event: ReactPointerEvent<HTMLButtonElement>) {
    event.currentTarget.classList.remove("holding");
    setActive(null);
  }

  function done() {
    finishActive(performance.now());
    props.onStopAudio();
    setStage("result");
  }

  function undoLast() {
    if (active) return;
    setRawGestures((current) => current.slice(0, -1));
  }

  function clearAll() {
    setActive(null);
    setRawGestures([]);
    sequenceRef.current = 1;
  }

  function back() {
    props.onStopAudio();
    setActive(null);
    props.onBack();
  }

  const timelineGestures = active
    ? [...rawGestures, { id: -1, startTime: active.startTime, endTime: Math.max(active.startTime + 1, visualNow) }]
    : rawGestures;
  const timelineStart = timelineGestures[0]?.startTime ?? 0;
  const timelineEnd = Math.max(timelineStart + beatMs * countInBeats, ...timelineGestures.map((gesture) => gesture.endTime));
  const timelineSpan = Math.max(1, timelineEnd - timelineStart);
  const headline = props.purpose === "rhythm-refine"
    ? "録った声を聞いて譜割りを整える"
    : props.purpose === "rhythm-first"
      ? "先に音の始まりと長さを作る"
      : "歌う予定のメロディを指で教える";

  if (stage === "result") {
    return <div className="melody-tap-guide">
      <div className="melody-tap-result"><span>メロディの細かさ</span><strong>{analysis.densityLabel}</strong><p>{analysis.onsetCount}回の音の開始をraw timingで記録しました。Pitchは推測していません。</p></div>
      <div className="melody-tap-recommendation"><span>おすすめ</span><strong>GRID {analysis.recommendation.grid}</strong><ul><li>短い音を {analysis.recommendation.grid} 相当まで残す</li><li>音の切り替わりを {analysis.recommendation.noteDetail >= 80 ? "細かめ" : analysis.recommendation.noteDetail <= 45 ? "滑らかめ" : "標準"} に見る</li>{analysis.repeatedOnsetIntent && <li>押し直した各音を別の開始として残す</li>}</ul></div>
      <details><summary>Advanced details</summary><dl><div><dt>最短の有意な区間</dt><dd>{analysis.shortestSignificantBeats.toFixed(3)} beat / {Math.round(analysis.shortestSignificantMs)} ms</dd></div><div><dt>推定最小譜割り</dt><dd>{analysis.recommendedDivision}</dd></div><div><dt>平均ノート長</dt><dd>{analysis.averageNoteLengthBeats.toFixed(2)} beats</dd></div><div><dt>最大保持</dt><dd>{analysis.maximumHoldBeats.toFixed(2)} beats</dd></div></dl></details>
      <div className="melody-tap-result-actions"><button onClick={() => void start(true)}>やり直す</button>{createMode ? <button className="concierge-primary" onClick={() => props.onCreate?.(rawGestures, analysis)}>{props.purpose === "rhythm-refine" ? "この譜割りで再生成" : "EDITへ作る"}</button> : props.recommendationApplied ? <button onClick={props.onRestore}>元の設定へ戻す</button> : <button onClick={() => props.onApply(analysis)}>この設定を使う</button>}<button onClick={back}>閉じる</button></div>
    </div>;
  }

  return <div className="melody-tap-guide">
    <div className="melody-tap-copy">
      <span>{purposeLabel} · {props.targetLabel ?? "SONG START"}</span>
      <strong>{headline}</strong>
      <p>Tap/Holdのraw時刻を曲のBPM・PPQへ変換します。音が変わるたびに押してください。</p>
    </div>
    {props.purpose === "rhythm-first" && <section className="melody-tempo-control" aria-label="リズム入力のBPM設定">
      <div><span>TEMPO</span><strong>{props.bpm} BPM</strong><small>40–240</small></div>
      <div className="bpm-number-field"><span>BPM</span><BpmNumberInput bpm={props.bpm} onChange={(value) => props.onBpmChange?.(value)} disabled={stage !== "intro"} ariaLabel="リズム入力 BPM" /></div>
      <button type="button" onClick={props.onTapTempo} disabled={stage !== "intro"}>TAP TEMPO<small>{(props.tapCount ?? 0) > 1 ? `${props.tapCount} taps` : "2回以上タップ"}</small></button>
    </section>}
    {!props.bpmConfirmed && <p className="melody-tap-warning">現在は {props.bpm} BPM を基準にします。必要なら先にBPMを確認してください。</p>}
    <div className="melody-tap-clock" aria-live="polite">
      <span><small>BPM</small><b>{props.bpm}</b></span>
      <span><small>COUNT-IN</small><b>{stage === "intro" ? "READY" : stage === "counting" ? props.count ?? "…" : "BAR"}</b></span>
      <span><small>BEAT</small><b>{stage === "tapping" ? liveBeat : props.count ?? "—"}</b></span>
      <div className="melody-beat-indicators" aria-label="カウント位置">
        {Array.from({ length: countInBeats }, (_, index) => <i key={index} className={stage === "counting" && index === countBeat ? "active" : ""} />)}
      </div>
    </div>
    {createMode && props.onPickupChange && <label className="melody-pickup-toggle"><input type="checkbox" checked={Boolean(props.pickupEnabled)} onChange={(event) => props.onPickupChange?.(event.target.checked)} /><span>PICKUP CAPTURE</span><strong>{props.pickupEnabled ? "ON" : "OFF"}</strong><small>{props.pickupEnabled ? "カウント中のTap/Holdも弱起として保存" : "カウントは準備だけ"}</small></label>}
    <div className="melody-tap-pad-shell">
      {stage === "counting" && <div className="melody-tap-count-overlay" role="status" aria-label={"Count " + (props.count ?? "")}><strong>{props.count ?? "…"}</strong><span>{props.pickupEnabled ? "PICKUP ARMED" : `準備 · ${props.targetLabel ?? "BAR 1"}からタップ開始`}</span></div>}
      <button
        className={active ? "melody-tap-pad holding" : "melody-tap-pad"}
        disabled={!canTap}
        onPointerDown={pointerDown}
        onPointerUp={pointerUp}
        onPointerCancel={pointerCancel}
        onContextMenu={(event) => event.preventDefault()}
      >
        <strong>{active ? "HOLD…" : "TAP / HOLD"}</strong>
        <span>{active ? Math.round(visualNow - active.startTime) + " ms" : stage === "intro" ? "Padはこの位置のまま · START後に入力" : stage === "counting" && !props.pickupEnabled ? `準備中 · ${props.targetLabel ?? "BAR 1"}からタップ開始` : "音が変わるたびに押す"}</span>
      </button>
    </div>
    {!!timelineGestures.length && <div className="melody-tap-timeline" aria-label="入力したメロディー譜割り">
      {timelineGestures.map((gesture) => {
        const left = (gesture.startTime - timelineStart) / timelineSpan * 100;
        const width = Math.max(1.5, (gesture.endTime - gesture.startTime) / timelineSpan * 100);
        return <i key={gesture.id} className={gesture.id === -1 ? "active" : ""} style={{ left: left + "%", width: width + "%" }} />;
      })}
    </div>}
    {stage === "intro"
      ? <div className="melody-tap-intro-actions"><button onClick={back}>CANCEL</button><button className="concierge-primary" onClick={() => void start(false)}>START</button></div>
      : <><div className="melody-tap-live-head"><div><span>{stage === "counting" ? "COUNT-IN · raw input" : purposeLabel + " · " + props.bpm + " BPM"}</span><strong>{rawGestures.length + (active ? 1 : 0)} ONSETS</strong></div><button onClick={done} disabled={rawGestures.length + (active ? 1 : 0) < 1}>DONE / STOP</button></div><div className="melody-tap-edit-actions"><button onClick={undoLast} disabled={!rawGestures.length || Boolean(active)}>1つ戻す</button><button onClick={clearAll} disabled={!rawGestures.length && !active}>全消去</button><button onClick={() => void start(true)}>やり直す</button><button onClick={back}>CANCEL</button></div></>}
    <small>1回押すたびに1音の開始。長押しは音の長さ。raw timestamp / raw tickを保持します。</small>
  </div>;
}
